import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Show } from '@components/Show';
import Tooltip from '@components/Tooltip';
import { CircleIconWrapper } from '@ContractBuilder/contract-builder-header/components/CircleIconWrapper';
import { ViewPath } from '@ContractBuilder/contract-builder-header/constants';
import { useBlockEditFormStore } from '@ContractBuilder/modules/block-edit';
import { DrawerLayoutComponent } from '@ContractBuilder/modules/drawer-layout';
import { useEntityStore, useSchemaStore } from '@ContractBuilder/store';
import { useDatapointsStore } from '@ContractBuilder/store/datapoints.store';
import { useDrawerStore } from '@ContractBuilder/store/drawer.store';
import type { BlockError } from '@domain/schemas/block-error';
import { ContractError } from '@domain/types/errors';
import { ValidationStatusBadge } from '@features/contract-validation/check-validation-panel/components/ValidationStatusBadge';
import { DocumentMode } from '@helpers/document-mode';
import { getDocumentMode } from '@helpers/getDocumentMode';
import { getBlockValidationErrors, getContractValidationErrors } from '@helpers/status';
import { ShieldCheckIcon } from '@heroicons/react/outline';
import { isPPLFrontend } from '@root/src/utils/app-paths';
import clsx from 'clsx';

const getType = (
  blockErrors: BlockError[],
  contractErrors: { type: ContractError; error: Record<string, string>[] }[],
) => {
  if (blockErrors.length > 0 || contractErrors.some((error) => error.type === ContractError.InvalidForm)) {
    return 'danger';
  }
  if (contractErrors.some((error) => error.type === ContractError.MissingDatapoints)) {
    return 'warning';
  }
  return 'valid';
};

export const CheckValidation = () => {
  const editingBlockId = useBlockEditFormStore((state) => state.formValues?.id);
  const { closeDrawer, isDrawerOpen, openDrawer, state } = useDrawerStore(
    ({ closeDrawer, isOpen, openDrawer, state }) => ({
      closeDrawer,
      isDrawerOpen: isOpen,
      openDrawer,
      state,
    }),
  );
  const { submission } = useEntityStore(({ submission }) => ({
    submission,
  }));
  const { pathname } = useLocation();

  const { mode } = getDocumentMode();

  const isSubmission = mode === DocumentMode.SUBMISSIONS;
  const isEndorsement = mode === DocumentMode.ENDORSEMENTS;

  const draftCheckValidation = !isPPLFrontend && isSubmission;

  const isCheckValidationDrawerOpen = isDrawerOpen && state?.type === DrawerLayoutComponent.CheckValidation;

  const isEditing = editingBlockId !== undefined;

  const handleCheckValidationDrawerToggleClick = () => {
    if (isDrawerOpen && isCheckValidationDrawerOpen) {
      return closeDrawer();
    }

    return openDrawer({ type: DrawerLayoutComponent.CheckValidation, props: {} });
  };

  useEffect(() => {
    const currentPath = pathname.split('/').pop() as ViewPath;
    if (![ViewPath.Contract, ViewPath.Endorsements].includes(currentPath)) {
      closeDrawer();
    }
    // eslint-disable-next-line -- We only want to run this hook when switching between contract and form views
  }, [pathname]);

  const { isValid, validate } = useSchemaStore(({ isValid, validate }) => ({
    isValid,
    validate,
  }));

  const { formValues } = useDatapointsStore(({ formValues }) => ({
    formValues,
  }));

  useEffect(() => {
    validate();
  }, [formValues, validate]);

  if (!submission) {
    return null;
  }

  const blockErrors = getBlockValidationErrors(isSubmission || isEndorsement, submission);

  const contractErrors = getContractValidationErrors(isSubmission || isEndorsement, isValid, submission);

  const type = getType(blockErrors, contractErrors);

  const showBadge = submission.version > 2 || (submission.data_items && Object.keys(submission.data_items).length > 0);

  return (
    <div className="flex gap-x-3">
      <Show when={draftCheckValidation}>
        <ValidationStatusBadge type={type} showBadge={showBadge}>
          <Tooltip content="Check validation" disabled={isEditing}>
            <CircleIconWrapper
              icon={ShieldCheckIcon}
              isActive={isCheckValidationDrawerOpen}
              onClick={handleCheckValidationDrawerToggleClick}
              wrapperClassName={clsx(isEditing && 'pointer-events-none bg-transparent opacity-50')}
              data-testid="check-validation-icon"
            />
          </Tooltip>
        </ValidationStatusBadge>
      </Show>
    </div>
  );
};
